import type {
  AnalyticsOverview,
  Granularity,
  TimeseriesMetric,
  TimeseriesResponse,
  TopVideosResponse,
  TrafficSourcesResponse,
  WatchTimeByChannel,
} from '@qlk/shared';
import type { MocSoLieu } from '@qlk/shared';
import { useQuery } from '@tanstack/react-query';

import { api, type LoiApi } from '../../lib/api.js';

const GOC = '/api/v1/analytics';

export interface BoLocPhanTich {
  from: string;
  to: string;
  /**
   * **Mã kỳ** của khoảng (`KyTrang.ma`) — để máy chủ suy "kỳ trước" theo đúng
   * luật của `@qlk/shared/ky.ts` thay vì chỉ lùi số ngày.
   */
  ky?: string;
  /** Rỗng nghĩa là mọi kênh người dùng được xem. */
  channelIds?: string[];
  groupId?: string;
}

export const KHOA = {
  tongQuan: (loc: BoLocPhanTich) => ['analytics', 'overview', loc] as const,
  gioXem: (loc: BoLocPhanTich) => ['analytics', 'watch-time', loc] as const,
  nguonLuuLuong: (loc: BoLocPhanTich) => ['analytics', 'traffic', loc] as const,
  videoNoiBat: (loc: BoLocPhanTich, limit: number) =>
    ['analytics', 'top-videos', loc, limit] as const,
  chuoiThoiGian: (loc: BoLocPhanTich, metric: string, granularity: string) =>
    ['analytics', 'timeseries', loc, metric, granularity] as const,
};

function thamSo(loc: BoLocPhanTich, them: Record<string, string> = {}): string {
  const sp = new URLSearchParams({ from: loc.from, to: loc.to, ...them });
  if (loc.ky !== undefined && loc.ky !== '') sp.set('ky', loc.ky);
  if (loc.channelIds && loc.channelIds.length > 0) sp.set('channelIds', loc.channelIds.join(','));
  if (loc.groupId) sp.set('groupId', loc.groupId);
  return `?${sp.toString()}`;
}

export function useTongQuanPhanTich(loc: BoLocPhanTich) {
  return useQuery<AnalyticsOverview, LoiApi>({
    queryKey: KHOA.tongQuan(loc),
    queryFn: () => api.get<AnalyticsOverview>(`${GOC}/overview${thamSo(loc)}`),
  });
}

export function useGioXemTheoKenh(loc: BoLocPhanTich) {
  return useQuery<WatchTimeByChannel, LoiApi>({
    queryKey: KHOA.gioXem(loc),
    queryFn: () => api.get<WatchTimeByChannel>(`${GOC}/watch-time-by-channel${thamSo(loc)}`),
  });
}

/**
 * Khoảng đã sẵn sàng để gọi API hay chưa.
 *
 * Lượt render **đầu tiên** của trang chưa biết mốc dữ liệu nên `from`/`to` còn
 * rỗng; gửi `?to=` rỗng là chắc chắn 400 (`analyticsRangeQuerySchema` đòi
 * `YYYY-MM-DD`). Một 400 vô ích mỗi lần mở trang làm log khó đọc và làm người
 * gỡ lỗi tưởng có gì hỏng.
 */
function khoangSanSang(loc: BoLocPhanTich): boolean {
  return loc.from !== '' && loc.to !== '';
}

export function useNguonLuuLuong(loc: BoLocPhanTich) {
  return useQuery<TrafficSourcesResponse, LoiApi>({
    queryKey: KHOA.nguonLuuLuong(loc),
    queryFn: () => api.get<TrafficSourcesResponse>(`${GOC}/traffic-sources${thamSo(loc)}`),
    enabled: khoangSanSang(loc),
  });
}

export function useVideoNoiBat(loc: BoLocPhanTich, limit = 10) {
  return useQuery<TopVideosResponse, LoiApi>({
    queryKey: KHOA.videoNoiBat(loc, limit),
    queryFn: () =>
      api.get<TopVideosResponse>(`${GOC}/top-videos${thamSo(loc, { limit: String(limit) })}`),
    enabled: khoangSanSang(loc),
  });
}

export function useChuoiThoiGian(
  loc: BoLocPhanTich,
  metric: TimeseriesMetric = 'views',
  granularity: Granularity = 'day',
) {
  return useQuery<TimeseriesResponse, LoiApi>({
    queryKey: KHOA.chuoiThoiGian(loc, metric, granularity),
    queryFn: () =>
      api.get<TimeseriesResponse>(`${GOC}/timeseries${thamSo(loc, { metric, granularity })}`),
  });
}

/**
 * Mốc dữ liệu: hai đầu trục thời gian **đã có số liệu**, kèm **các năm có số
 * liệu** cho nhóm "Theo năm" của ô chọn kỳ.
 *
 * Bộ chọn kỳ dựng khoảng từ mốc này thay vì lùi từ "hôm qua" — YouTube
 * Analytics trễ 1–3 ngày, lấy mốc hôm qua thì khung "28 ngày qua" của app ngắn
 * hơn 28 ngày thật của YouTube Studio.
 *
 * `channelId` thu phạm vi về một kênh (trang chi tiết kênh, trang Hiệu suất
 * khi đang lọc một kênh): ô chọn kỳ ở đó chỉ được bày năm của **kênh đang
 * xem**. Nó nằm trong `queryKey` nên mỗi kênh có bản cache riêng.
 *
 * Cache 5 phút, và được dọn sau khi đồng bộ YouTube hoặc thêm kênh — xem
 * `KHOA_MOC_SO_LIEU`.
 */
export function useMocSoLieu(bat = true, channelId?: string) {
  const duong = channelId === undefined ? '' : `?channelId=${encodeURIComponent(channelId)}`;
  return useQuery<MocSoLieu, LoiApi>({
    queryKey: [...KHOA_MOC_SO_LIEU, channelId ?? 'tat-ca'],
    queryFn: () => api.get<MocSoLieu>(`${GOC}/moc${duong}`),
    /* Endpoint sau cổng `analytics:view`: người thiếu quyền (biên tập, cộng
       tác viên) thì **không gọi** thay vì bắn một request chắc chắn 403 mỗi
       lần mở trang. Chỗ gọi tự lo phần "chưa biết mốc" (xem `kyTuMa`). */
    enabled: bat,
    staleTime: 5 * 60_000,
  });
}

/**
 * Khoá cache của mốc dữ liệu — **tiền tố**, mọi phạm vi kênh nằm dưới nó.
 *
 * Module khác dọn bằng khoá này sau khi đồng bộ YouTube (`sync/api.ts`) hoặc
 * thêm kênh (`channels/api.ts`): thêm một kênh có số liệu từ 2024 thì ô chọn
 * kỳ phải có 2024 ngay, không chờ hết 5 phút `staleTime`.
 */
export const KHOA_MOC_SO_LIEU = ['analytics', 'moc'] as const;
